import { MessageDescriptor } from 'react-intl'

import { cloneDeep, isEmpty } from 'lodash-es'

import {
  DOMAIN_TO_TITLE,
  PROVIDER_TAB_CONTENT_CONFIGURATION_MSGS,
  PROVIDER_TAB_CONTENT_LOW_SCORE_MSGS,
  SOCIAL_NEED_OPTIONS,
  WellnessCheckInDomain,
  WellnessCheckInDomainToTitle,
} from './constants'
import { WellbeingResponse, WellbeingResponseResults, WellnessCheckInResponse, WellnessCheckInResults } from './types'
import { WELLNESS_CHECK_IN_INITIAL_VALUES, WELLNESS_CHECK_IN_NUM_DOMAINS } from './wellnessCheckInMetadata'

export const getWellnessCheckInTotalNumPages = (socialCareEnabled: boolean) => {
  const howAreYouFeelingPage = 1 // first page of the wellness check in flow
  const socialCarePage = 1 // last page of the wellness check in flow if customer has social care enabled
  const numPages = howAreYouFeelingPage + WELLNESS_CHECK_IN_NUM_DOMAINS
  return socialCareEnabled ? numPages + socialCarePage : numPages
}

export const getInitialWellnessCheckinValues = (socialCareEnabled: boolean) => {
  const initialValues = cloneDeep(WELLNESS_CHECK_IN_INITIAL_VALUES)
  if (!socialCareEnabled) {
    delete initialValues.socialCareSupport
  }
  return initialValues
}

export const getWellnessCheckInParsedBody = (
  wellnessCheckInResponse: WellnessCheckInResponse,
): WellnessCheckInResults => {
  const latestWellbeingResponseStrWithForm = wellnessCheckInResponse.body
  const results = JSON.parse(latestWellbeingResponseStrWithForm)
  return results
}

export const showSocialCareSupport = (socialCareOptions?: SOCIAL_NEED_OPTIONS[]): boolean => {
  return !(
    socialCareOptions === undefined ||
    socialCareOptions.length === 0 ||
    (socialCareOptions.length === 1 && socialCareOptions[0] === SOCIAL_NEED_OPTIONS.NONE_OF_THE_ABOVE)
  )
}

/**
 * Translate essential domain to display domain name
 * @param title
 */
export const getDomainDisplayName = (title: WellnessCheckInDomain): MessageDescriptor => {
  return WellnessCheckInDomainToTitle[title]
}

export const isInvalidDomain = (domain: WellnessCheckInDomain, results: WellbeingResponseResults) => {
  return results === undefined || results.length === 0 || getDomainDisplayName(domain) === undefined
}

/**
 * Return a valid wellbeing response by filtering out domains that are no longer supported / empty responses
 * @param wellbeingResponse
 */
export const getValidWellbeingResponse = (wellbeingResponse: WellbeingResponse) => {
  const validWellbeingResponse = cloneDeep(wellbeingResponse)
  const emptyDomains = Object.keys(validWellbeingResponse).filter((domain: string) => {
    const result = validWellbeingResponse[domain as WellnessCheckInDomain]
    return isInvalidDomain(domain as WellnessCheckInDomain, result.response)
  })

  // Filter out domains with no response
  emptyDomains.map((domain: string) => {
    delete validWellbeingResponse[domain as WellnessCheckInDomain]
  })

  return validWellbeingResponse
}

/**
 *  Given a WellnessCheckInResults object, return true if the results are valid.
 *  Returns false if results are empty (user skipped all) or invalid (user answered some, but not enough to determine results)
 */
export const isValidWellnessCheckInResults = (wellnessCheckInResults: WellnessCheckInResults | undefined) => {
  return (
    wellnessCheckInResults?.wellbeingResponse &&
    !isEmpty(getValidWellbeingResponse(wellnessCheckInResults.wellbeingResponse))
  )
}

export const getProviderTabLowScoreMsg = (lowScoreCount: number) => {
  if (lowScoreCount === 0) {
    return PROVIDER_TAB_CONTENT_LOW_SCORE_MSGS.noLowScores
  } else if (lowScoreCount === 1) {
    return PROVIDER_TAB_CONTENT_LOW_SCORE_MSGS.oneLowScore
  } else if (lowScoreCount === 2) {
    return PROVIDER_TAB_CONTENT_LOW_SCORE_MSGS.twoLowScores
  } else {
    return PROVIDER_TAB_CONTENT_LOW_SCORE_MSGS.moreThanTwoLowScores
  }
}

export const getLowerCaseDomainTitle = (domain: WellnessCheckInDomain) => {
  return DOMAIN_TO_TITLE[domain].toLowerCase()
}

export const getProviderTabConfigurationMsg = ({ isDependentDisabled }: { isDependentDisabled: boolean }) => {
  return isDependentDisabled
    ? PROVIDER_TAB_CONTENT_CONFIGURATION_MSGS.eapWithoutDependents
    : PROVIDER_TAB_CONTENT_CONFIGURATION_MSGS.eapWithDependents
}
